import { css, LitElement, html } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
import { ScopedElementsMixin } from '@open-wc/scoped-elements';

var css_248z = css`/* Grid */
*,
*::before,
*::after {
  box-sizing: border-box;
}

.no-gutters {
  margin-right: 0;
  margin-left: 0;
}

.no-gutters > .col, .no-gutters > [class*=col-] {
  padding-right: 0;
  padding-left: 0;
}

/* Spacing */
.px-2 {
  padding-left: 16px !important;
  padding-right: 16px !important;
}

.spt-2 {
  padding-top: 12px !important;
}

@media (min-width: 768px) {
  .spb-md-2 {
    padding-bottom: 12px !important;
  }
}
/* Grid */
/* Utilities */
.text-center {
  text-align: center !important;
}

.d-flex {
  display: flex !important;
}

.overflow-x-scroll {
  overflow-x: scroll !important;
}

@media (min-width: 576px) {
  .d-sm-none {
    display: none !important;
  }
}
.justify-content-between {
  -ms-flex-pack: justify !important;
  justify-content: space-between !important;
}

.flex-grow-1 {
  -ms-flex-positive: 1 !important;
  flex-grow: 1 !important;
}

.justify-content-start {
  -ms-flex-pack: start !important;
  justify-content: flex-start !important;
}

.d-none {
  display: none !important;
}

.flex-wrap {
  -ms-flex-wrap: wrap !important;
  flex-wrap: wrap !important;
}

@media (min-width: 992px) {
  .flex-lg-nowrap {
    -ms-flex-wrap: nowrap !important;
    flex-wrap: nowrap !important;
  }
}
.text-nowrap {
  white-space: nowrap !important;
}

.flex-column {
  -ms-flex-direction: column !important;
  flex-direction: column !important;
}

.tab-list {
  font-family: "Roboto";
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 24px;
  color: #00716b;
  height: 48px;
  outline: none;
}

/* Hide scrollbar for Chrome, Safari and Opera */
.tab-list::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.tab-list {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

.tab:focus:not(:focus-visible) {
  outline: none;
}

.tab-divider {
  border-bottom: 2px solid #dedede;
}

.tab:hover {
  color: #00857a;
  font-weight: 400;
  cursor: pointer;
}

.tab.selected,
.tab.selected:hover {
  color: #005e5d;
  font-weight: 400;
  border-bottom: 2px solid #005e5d;
  cursor: inherit;
}

.tab.disabled,
.tab.disabled:hover {
  color: #999;
  cursor: inherit;
}

.tab-fade-area, .tab-fade-area-right, .tab-fade-area-left {
  min-width: 48px;
  position: sticky;
  position: -webkit-sticky;
  pointer-events: none;
}

.tab-fade-area-left {
  left: 0;
}

.tab-fade-area-right {
  right: 0;
}

.gradient-white-right {
  background: linear-gradient(to right, rgba(255, 255, 255, 0) 0%, #fff 100%);
}

.gradient-n50-right {
  background: linear-gradient(to right, rgba(249, 249, 249, 0) 0%, #f9f9f9 100%);
}

.gradient-white-left {
  background: linear-gradient(to left, rgba(255, 255, 255, 0) 0%, #fff 100%);
}

.gradient-n50-left {
  background: linear-gradient(to left, rgba(249, 249, 249, 0) 0%, #f9f9f9 100%);
}

@media only screen and (min-width: 992px) {
  .vertical-tabs {
    flex-direction: column;
    height: auto;
  }
  .vertical-tabs .tab:hover, .vertical-tabs .tab:focus {
    background-color: #edf7f7;
  }
  .vertical-tabs .tab.selected {
    background-color: #d8f0ef;
    border-bottom: 0px;
    border-left: 2px solid #005e5d;
    /* !important is used in order to overwrite the styles used from sc-styles-spacing library */
    padding-left: 14px !important;
  }
  .overflow-x-visible {
    /* !important is used in order to overwrite the styles used from sc-styles-utilities library */
    overflow-x: visible !important;
  }
}`;

class AabTabs extends ScopedElementsMixin(LitElement) {
  constructor() {
    super();
    this.tabs = [];
    this.selected = '';
    this.fullWidth = false;
    this.fadeWhite = false;
    this.scrolling = false;
    this.vertical = false;
    this.divider = false;
  }

  static get properties() {
    return {
      tabs: { type: Array },
      selected: { type: String },
      scrolling: { type: Boolean },
      vertical: { type: Boolean },
      fullWidth: {
        type: Boolean,
        attribute: 'full-width',
      },
      fadeWhite: {
        type: Boolean,
        attribute: 'fade-white',
      },
      divider: { type: Boolean },
    };
  }

  static get styles() {
    return [css_248z];
  }

  static get scopedElements() {
    return {};
  }

  firstUpdated() {
    if (this.scrolling) {
      this.checkHorizontalScroll();
    }
  }

  selectTab(tab) {
    if (!tab.disabled && this.selected !== tab.id) {
      this.selected = tab.id || tab.title;
    }
  }

  handleKeyPress(tab, event) {
    if (event.key === 'Enter' || event.key === ' ') {
      this.selectTab(tab);
    }
    // To prevent the page to scroll down when space bar is used
    event.preventDefault();
  }

  // check if the div has horizontal scrolling and add/remove the class
  checkHorizontalScroll() {
    const tabList = this.shadowRoot.querySelector('.tab-list');
    const fadeAreaRight = this.shadowRoot.querySelector('.tab-fade-area-right');
    const horizontalScroll = tabList.scrollWidth > tabList.clientWidth;
    if (horizontalScroll) {
      fadeAreaRight.classList.remove('d-none');
    } else {
      fadeAreaRight.classList.add('d-none');
    }
  }

  // scroll handler, adds and removes the class based on scroll positions
  scrollHandler(e) {
    if (this.scrolling) {
      const fadeAreaRight = this.shadowRoot.querySelector('.tab-fade-area-right');
      const fadeAreaLeft = this.shadowRoot.querySelector('.tab-fade-area-left');
      if (fadeAreaRight) {
        const scrollEnd = e.target.scrollWidth - e.target.clientWidth === e.target.scrollLeft;
        if (scrollEnd) {
          fadeAreaRight.classList.add('d-none');
        } else {
          fadeAreaRight.classList.remove('d-none');
        }
      }
      if (fadeAreaLeft) {
        if (e.target.scrollLeft > 0) {
          fadeAreaLeft.classList.remove('d-none');
        } else {
          fadeAreaLeft.classList.add('d-none');
        }
      }
    }
  }

  tabClasses(tab) {
    return {
      'tab-divider': this.divider,
      'flex-grow-1': this.fullWidth,
      'spb-md-2': this.vertical,
      'disabled': tab.disabled,
      'selected': this.selected === tab.id || this.selected === tab.title
    };
  }

  render() {
    const containerClasses = {
      'overflow-x-scroll': !this.vertical && this.scrolling,
      'overflow-x-visible': this.vertical && this.scrolling,
      'justify-content-between': !this.vertical && this.fullWidth,
      'text-center': !this.vertical && this.fullWidth,
      'flex-wrap': !this.vertical && !this.scrolling,
      'flex-lg-nowrap': this.vertical,
      'vertical-tabs': this.vertical,
    };

    return html`
      <div class="d-flex no-gutters tab-list text-nowrap justify-content-start ${classMap(containerClasses)}" @scroll=${this.scrollHandler}>
      ${this.scrolling ? html` <span class="tab-fade-area-left d-none ${this.fadeWhite ? 'gradient-white-left' : 'gradient-n50-left'} "></span>` : ''} 
        ${this.tabs.map((tab) => html`
          <a role="tab" tabindex="0"
            @click=${() => this.selectTab(tab)}
            @keypress=${(event) => this.handleKeyPress(tab, event)}
            class="px-2 spt-2 tab ${classMap(this.tabClasses(tab))}">
              ${tab.title}
          </a>
        `)}
        ${this.scrolling ? html` <span class="tab-fade-area-right d-none ${this.fadeWhite ? 'gradient-white-right' : 'gradient-n50-right'} "></span>` : ''} 
      </div>
    `;
  }
}

export { AabTabs as default };
